import { Components, Formio } from 'formiojs';
import FieldComponent from 'formiojs/components/_classes/field/Field';

declare let $: any;
declare let moment: any;

export class DateRangeComponent extends Components.components.field {
  declare refs: {
    dateRangeInput?: HTMLInputElement;
  };

  triggerChange: any;

  static override schema(): any {
    return super.schema({
      type: 'daterange',
      label: 'Date Range',
      key: 'dateRange',
      input: true,
      onlyFutureDates: false,
      dateMode: 'datetime',
      validate: {
        required: true
      }
    });
  }

  static get builderInfo() {
    return {
      title: 'Date Range',
      group: 'basic',
      icon: 'calendar',
      weight: 70,
      documentation: '',
      schema: DateRangeComponent.schema()
    };
  }



  /** ✅ Add checkbox directly in Display tab */
  static editForm(...extend: any[]) {
    const editForm = FieldComponent.editForm(...extend);

    function findDisplayTab(components: any[]): any {
      for (const comp of components) {
        if (comp.key === 'display' && Array.isArray(comp.components)) {
          return comp;
        }
        if (Array.isArray(comp.components)) {
          const found = findDisplayTab(comp.components);
          if (found) return found;
        }
      }
      return null;
    }

    const displayTab = findDisplayTab(editForm.components);

    if (displayTab) {

      displayTab.components.push({
        type: 'radio',
        key: 'dateMode',
        label: 'Date/Time Mode',
        values: [
          { label: 'Date Only', value: 'date' },
          { label: 'Time Only', value: 'time' },
          { label: 'Date + Time', value: 'datetime' }
        ],
        defaultValue: 'datetime',
        inline: true,
        weight: 90,
        input: true
      });

      displayTab.components.push({
        type: 'checkbox',
        key: 'onlyFutureDates',
        label: 'Allow only future dates',
        tooltip: 'If checked, user can select only future dates',
        input: true,
        weight: 100
      });
    }

    return editForm;
  }

  override render(): string {
    return super.render(`<input type="text" class="form-control" ref="dateRangeInput" />`);
  }

  override attach(element: HTMLElement): any {
    this.loadRefs(element, {
      dateRangeInput: 'single'
    });

    const inputEl = this.refs.dateRangeInput as HTMLInputElement;

    if (inputEl) {
      setTimeout(() => {
        const mode = this.component.dateMode || 'datetime';

        let format = 'YYYY-MM-DD';
        let timePicker = false;

        if (mode === 'time') {
          format = 'hh:mm A';
          timePicker = true;
        } else if (mode === 'datetime') {
          format = 'YYYY-MM-DD hh:mm A';
          timePicker = true;
        }

        // ✅ Add ranges here
        $(inputEl).daterangepicker({
          locale: { format },
          opens: 'right',
          showDropdowns: true,
          autoApply: true,
          timePicker,
          timePicker24Hour: false,
          timePickerIncrement: 15,
          minDate: this.component.onlyFutureDates ? moment() : null,
          singleDatePicker: mode === 'time',
          ranges: mode !== 'time' ? {   // 👈 Only show ranges if not "time only"
            'Today': [moment(), moment()],
            'Yesterday': [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
            'Last 7 Days': [moment().subtract(6, 'days'), moment()],
            'Last 10 Days': [moment().subtract(9, 'days'), moment()],
            'This Month': [moment().startOf('month'), moment().endOf('month')],
            'Last Month': [
              moment().subtract(1, 'month').startOf('month'),
              moment().subtract(1, 'month').endOf('month')
            ]
          } : undefined
        }, (start: any, end: any) => {
          this.setValue({
            from: start.format(format),
            to: mode === 'time' ? start.format(format) : end.format(format)
          });
          this.triggerChange?.();
        });
      }, 100);
    }

    return super.attach(element);
  }


  override getValue(): any {
    return this.dataValue || { from: '', to: '' };
  }

  override setValue(value: any): boolean {
    if (!value || typeof value !== 'object') return false;
    this.dataValue = value;
    return true;
  }

  validate(required: boolean): boolean {
    const value = this.getValue();

    if (!value || !value.from || !value.to) {
      return !required;
    }

    if (this.component.onlyFutureDates) {
      const today = moment().startOf('day');
      const fromDate = moment(value.from);
      const toDate = moment(value.to);

      if (fromDate.isBefore(today) || toDate.isBefore(today)) {
        this.setCustomValidity('Please select only future dates.', true);
        return false;
      }
    }

    this.setCustomValidity('', true);
    return true;
  }

  updateValue(): void {
    this.dataValue = this.getValue();
    this.triggerChange();
  }
}

// ✅ Register globally
Formio.use({
  components: {
    daterange: DateRangeComponent
  }
});
